import React, { useEffect, useState } from 'react';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './styles/UserDetailModal.css';
import { formatFirebaseTimestamp } from '../../utils/datetime';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../firebase/config';

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
);

const DetailRow = ({ label, value }) => (
    <div className="ud-detail-row">
        <span className="ud-detail-label">{label}</span>
        <span className="ud-detail-value">{value ?? '—'}</span>
    </div>
);

const BoolPill = ({ value }) => (
    <span className={`ud-bool-pill ${value ? 'yes' : 'no'}`}>
        {value ? 'Yes' : 'No'}
    </span>
);

const PlatformPill = ({ platform }) => {
    if (!platform) return '—';
    const isIos = platform.toLowerCase().includes('ios');
    return (
        <span className={`ud-platform-pill ${isIos ? 'ios' : 'android'}`}>
            {isIos ? 'iOS' : 'Android'}
        </span>
    );
};

const SectionLabel = ({ children }) => (
    <div className="ud-section-label">{children}</div>
);

const STORAGE_PATH_RE = /^profilePictures\/.+\.jpg$/;

export default function UserDetailModal({ user, onClose }) {
    const [resolvedPhotoURL, setResolvedPhotoURL] = useState(null);
    const [photoLoaded, setPhotoLoaded] = useState(false);
    const [photoFetching, setPhotoFetching] = useState(false);
    const [photoLightbox, setPhotoLightbox] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                if (photoLightbox) setPhotoLightbox(false);
                else onClose();
            }
        };
        document.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [onClose, photoLightbox]);

    useEffect(() => {
        setPhotoLoaded(false);
        setResolvedPhotoURL(null);
        if (!user?.photoURL) { setPhotoFetching(false); return; }
        if (STORAGE_PATH_RE.test(user.photoURL)) {
            setPhotoFetching(true);
            getDownloadURL(ref(storage, user.photoURL))
                .then((url) => { setResolvedPhotoURL(url); setPhotoFetching(false); })
                .catch(() => { setPhotoFetching(false); });
        } else {
            setResolvedPhotoURL(user.photoURL);
        }
    }, [user?.photoURL]);

    const showPhotoLoader = photoFetching || (!!resolvedPhotoURL && !photoLoaded);

    if (!user) return null;

    const displayName =
        user.firstName && user.surname
            ? `${user.firstName} ${user.surname}`
            : user.name || '—';

    const initials = displayName !== '—'
        ? displayName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
        : '?';

    return (
        <>
        {photoLightbox && (
            <div className="ud-photo-lightbox" onClick={() => setPhotoLightbox(false)}>
                <img src={resolvedPhotoURL} alt={displayName} className="ud-photo-lightbox-img" />
            </div>
        )}
        <div className="ud-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="ud-modal" role="dialog" aria-modal="true">
                <button className="ud-close" onClick={onClose} aria-label="Close">
                    <CloseIcon />
                </button>

                {/* Header / avatar section */}
                <div className="ud-header">
                    <div
                        className={`ud-avatar-wrap${resolvedPhotoURL ? ' ud-avatar-clickable' : ''}`}
                        onClick={resolvedPhotoURL ? () => setPhotoLightbox(true) : undefined}
                    >
                        <div className="ud-avatar-placeholder">
                            {initials}
                        </div>
                        {showPhotoLoader && <div className="ud-avatar-spinner" />}
                        {resolvedPhotoURL && (
                            <img
                                src={resolvedPhotoURL}
                                alt={displayName}
                                className={`ud-avatar${photoLoaded ? ' loaded' : ''}`}
                                onLoad={() => setPhotoLoaded(true)}
                                onError={() => { setResolvedPhotoURL(null); setPhotoFetching(false); }}
                            />
                        )}
                        {user.onboardingCompleted && (
                            <CheckCircleIcon className="ud-avatar-badge" sx={{ fontSize: '20px', color: '#22c55e' }} />
                        )}
                    </div>

                    <div className="ud-header-info">
                        <div className="ud-name">
                            {displayName}
                            {user.isTrainer && (
                                <FitnessCenterIcon sx={{ fontSize: '15px', color: 'var(--color-primary)', marginLeft: '6px', verticalAlign: 'middle', opacity: 0.85 }} />
                            )}
                        </div>
                        {user.email && <div className="ud-email">{user.email}</div>}
                        <div className="ud-badges">
                            <span className="ud-badge role">{user.role ?? 'user'}</span>
                            {user.isTrainer && <span className="ud-badge trainer">Trainer</span>}
                            <span className={`ud-badge onboarding ${user.onboardingCompleted ? 'done' : 'pending'}`}>
                                {user.onboardingCompleted ? 'Onboarded' : 'Not onboarded'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Details */}
                <div className="ud-body">
                    {user.bio && (
                        <div className="ud-bio">
                            <p>{user.bio}</p>
                        </div>
                    )}
                    {(user.vibe || user.favouriteExercise) && (
                        <div className="ud-vibe-row">
                            {user.vibe && (
                                <div className="ud-vibe-chip">
                                    <span className="ud-vibe-chip-label">Vibe</span>
                                    <span className="ud-vibe-chip-value">{user.vibe}</span>
                                </div>
                            )}
                            {user.favouriteExercise && (
                                <div className="ud-vibe-chip">
                                    <span className="ud-vibe-chip-label">Fav exercise</span>
                                    <span className="ud-vibe-chip-value">{user.favouriteExercise}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Account */}
                    <div className="ud-details">
                        <SectionLabel>Account</SectionLabel>
                        <DetailRow label="User ID" value={<span className="ud-mono">{user.id}</span>} />
                        <DetailRow label="Date joined" value={formatFirebaseTimestamp(user.createdAt)} />
                        <DetailRow label="Role" value={user.role ?? 'user'} />
                        <DetailRow label="Trainer" value={<BoolPill value={!!user.isTrainer} />} />
                        <DetailRow label="Accepted terms" value={<BoolPill value={!!user.acceptedTerms} />} />
                        <DetailRow label="Notifications" value={<BoolPill value={!!user.allowNotifications} />} />
                        {user.appMetaData?.platform && (
                            <DetailRow label="Platform" value={<PlatformPill platform={user.appMetaData.platform} />} />
                        )}
                    </div>

                    {/* Profile */}
                    {/* <div className="ud-details" style={{ marginTop: '6px' }}>
                        <SectionLabel>Profile</SectionLabel>
                        {user.phone && <DetailRow label="Phone" value={user.phone} />}
                        {user.gender && <DetailRow label="Gender" value={user.gender} />}
                        {user.dateOfBirth && <DetailRow label="Date of birth" value={user.dateOfBirth} />}
                        {user.location && <DetailRow label="Location" value={user.location} />}
                    </div> */}

                    {/* Fitness */}
                    {/* <div className="ud-details" style={{ marginTop: '6px' }}>
                        <SectionLabel>Fitness</SectionLabel>
                        {user.height && <DetailRow label="Height" value={user.height} />}
                        {user.weight && <DetailRow label="Weight" value={user.weight} />}
                        {user.fitnessGoal && <DetailRow label="Goal" value={user.fitnessGoal} />}
                        {user.activityLevel && <DetailRow label="Activity level" value={user.activityLevel} />}
                    </div> */}
                </div>
            </div>
        </div>
        </>
    );
}
